// @flow
import { isFunction, toPairs } from 'lodash';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Fetcher from 'components/Fetcher';
import { useManager } from 'collections';
import api from 'api';
import { addNotification } from 'apps/core/flash_notifications/actions';

import { Icon } from 'components/Icon';
import { Button } from 'bs';
import SubmitButton from 'components/SubmitButton';
import { getDOMParents } from 'utils/dom';
import AppTrail, { BreadcrumbTrail } from './AppTrail';
import collection from "apps/core/notifications/store";
import { stripLocalHostname } from 'utils/urls';

const StyledNav = styled.nav`
    padding: ${props => props.theme.navPaddingTopBottom} 1rem
        ${props => props.theme.navPaddingTopBottom} 0;
    .app-switcher {
        padding: 8px 11px;
        height: 40px;
        width: 40px;
        margin: -0.25rem 0 -0.35rem;
        svg:hover {
            opacity: 0.75;
        }
    }

    a,
    button {
        color: ${props => props.theme.color.text};
        text-transform: inherit;
        font-weight: 300;
    }

    .btn.btn-link {
        letter-spacing: 0;
        &:hover {
            color: inherit;
        }
    }

    .navbar-brand {
        padding-left: 1rem;
    }
    .dropdown-menu.show {
        border-color: ${props => props.theme.color.lightBorder};
        border-top: none;
        border-right: none;
        box-shadow: 0px 1px 0px 0px ${props => props.theme.color.light};
        margin: 0;
        padding: 0;
        position: absolute;
        left: auto;
        right: -14px;
        top: calc(${props => props.theme.navPaddingTopBottom} + 100%);
        small {
            opacity: 0.75;
            white-space: nowrap;
        }
        .dropdown-item {
            padding-bottom: 0.75rem;
            padding-top: 0.75rem;
            &:active {
                background-color: ${props => props.theme.color.light};
            }
        }
    }

    ul.pull-right {
        .notifications-bell {
            a {
                position: relative;
                &:before {
                    background: orange;
                    border-radius: 50%;
                    content: '';
                    display: block;
                    height: 8px;
                    position: absolute;
                    right: 5px;
                    top: 10px;
                    width: 8px;
                }
            }
        }
        .dropdown-menu {
            padding: 0;
            a {
                padding-bottom: 0.5rem;
                padding-top: 0.5rem;
                color: ${props => props.theme.color.text} !important;
            }
        }
        li {
            span.icon {
                font-size: 1.25rem;
            }
        }
    }
    a:hover {
        text-decoration: none;
    }
    h3 {
        font-size: 1.25rem;
        font-weight: 100;
        line-height: 1.5rem;
        margin: 0;
    }
    a {
        &.dropdown-toggle {
            span.icon {
                font-size: 1rem;
            }
        }
    }
    .app-icon {
        font-size: 2rem;
        opacity: 0.4;
        padding-right: 0.75rem;
        position: relative;
    }
`;

const Nav = ({ children }) => (
    <StyledNav className='navbar navbar-expand d-flex border-bottom border-gray-200 bg-white'>
        {children}
    </StyledNav>
);

const NavbarProps = {
    logout: PropTypes.func.isRequired,
    toggleSwitcher: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    apps: PropTypes.object.isRequired,
    currentApp: PropTypes.string,
    user: PropTypes.object.isRequired,
    appTrail: PropTypes.array.isRequired,
    appLocations: PropTypes.object.isRequired,
};

const clickFunction = func => e => {
    e.preventDefault();
    func();
};

const AppNavMenu = ({ app, location, user = null, extra = [], showAppItems = true, extraClass = "" }) => {
    if (!app) {
        return null;
    }

    let appItems = [];
    if (showAppItems) {
        // navMenu can be a list of static menu items
        // or a callable that will render menu items for a user
        appItems = app.navMenu || [];
        if (isFunction(appItems)) {
            appItems = appItems({user});
        }
    }


    const items = [...appItems, ...extra];

    if (items.length < 1) {
        return null;
    }

    return (
        <div className={`navbar-collapse collapse show d-print-none ${extraClass}`}>
            <div className='navbar-nav'>
                {items.map(({ path, label, icon = '', search = '' }) => {
                    const active = location.pathname.startsWith(path);
                    const className = `nav-item nav-link${
                        active ? ' active text-muted' : ''
                    }`;

                    return (
                        <Link to={{ pathname: path, search, state: { prevPath: location.pathname }}} key={path} className={className} >
                            {icon && <Icon className='mr-2' name={icon} />}
                            {label}
                            {active && <span className='sr-only'>(current)</span>}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

const AppItemsMenu = ({ app, location, user = null, extra = [] }) => {
    if (!app) {
        return null;
    }

    let appItems = [];
    // navMenu can be a list of static menu items
    // or a callable that will render menu items for a user
    appItems = app.navMenu || [];
    if (isFunction(appItems)) {
        appItems = appItems({user});
    }


    if (appItems.length < 1) {
        return null;
    }

    return (
        <>
            <span className="pull-right flex-row ml-auto">
                {appItems.map(({ path, label, icon = '', search = '' }) => {
                    const active = location.pathname.startsWith(path);
                    const className = `nav-item nav-link${
                        active ? ' active text-muted' : ''
                    }`;

                    return (
                        <Link to={{ pathname: path, search, state: { prevPath: location.pathname }}} key={path} className={className} >
                            {icon && <Icon className='mr-2' name={icon} />}
                            {label}
                            {active && <span className='sr-only'>(current)</span>}
                        </Link>
                    );
                })}
                </span>
          </>
    );
};


const ProfileLink = ({ user, profileUrl, profileLabel }) => (
    <Link className='dropdown-item text-gray-600' to={profileUrl}>
        {profileLabel}
        <br />
        <small>{user.email}</small>
    </Link>
);

const ProfileMenu = ({
    user,
    logout,
    profileUrl = '/profile',
    profileLabel = 'My Profile',
}) => {
    const [showMenu, setShowMenu] = useState(false);

    const toggleProfileMenu = event => {
        let _showMenu = !showMenu;

        // special case for clicking on the dropdown menu trigger itself
        const parents = getDOMParents(event.target, 'button', true);
        const isTrigger = e => e.id === 'navbarDropdownMenuLink';
        const clickedTrigger = parents.filter(isTrigger).length > 0;

        if (_showMenu || (!_showMenu && clickedTrigger)) {
            event.stopPropagation();
        }

        setShowMenu(_showMenu);
    };

    useEffect(() => {
        if (showMenu) {
            window.addEventListener('click', toggleProfileMenu, true);
        } else {
            window.removeEventListener('click', toggleProfileMenu, true);
        }

        return function cleanup() {
            window.removeEventListener('click', toggleProfileMenu, true);
        };
    });

    return (
        <ul className='navbar-nav pull-right ml-auto d-print-none'>
            <li className={`nav-item dropdown ${showMenu ? 'show' : ''}`}>
                <Button
                    flavor='link'
                    className='nav-link dropdown-toggle'
                    id='navbarDropdownMenuLink'
                    onClick={toggleProfileMenu}
                >
                    <span className='ml-2 d-none d-md-inline'>{user.name}</span>
                    <Icon className='ml-2 d-md-none' title={user.name} name='user' />
                </Button>
                <div
                    className={`dropdown-menu dropdown-menu-right ${
                        showMenu ? 'show' : ''
                    }`}
                >
                    <ProfileLink
                        user={user}
                        profileUrl={profileUrl}
                        profileLabel={profileLabel}
                    />
                    <Link
                        id='navbar-support-link'
                        className='dropdown-item text-gray-600'
                        to='/support'
                    >
                        Support
                    </Link>
                    <a
                        id='logout'
                        href='#logout'
                        className='dropdown-item text-gray-600'
                        onClick={clickFunction(logout.bind(this))}
                    >
                        Logout
                    </a>
                </div>
            </li>
        </ul>
    );
};


const DismissButton = ({ id, onChange }) => {
    const [fetching, setFetching] = useState(false);
    const apiUrl = `user/notifications/${id}/dismiss/`;
    const unlink = () => {
        setFetching(true);
        api.post(apiUrl)
            .then(resp => {
                setFetching(false);
                onChange();
            })
            .catch(error => {
                addNotification({message: `There was an error. ${error.message}`, flavor: 'danger'});
                setFetching(false);
            });
    };

    return (
        <SubmitButton
            className="ms-auto flex-shrink-0"
            flavor='light'
            submitting={fetching}
            onClick={unlink}
            label=""
            icon="check"
        />
    );
};


const NotificationItem = ({notification, onChange}) => {

    if (notification.url) {
        return (
            <div className="dropdown-item d-flex align-items-center justify-content-between">
                <Link
                    id={notification.id}
                    key={notification.id}
                    className='text-gray-600 pr-2'
                    to={stripLocalHostname(notification.url)}
                >
                    {notification.title}
                </Link>
                <DismissButton onChange={onChange} id={notification.id}/>
            </div>
        )
    } else {
        return (
            <span className='dropdown-item text-gray-600 d-flex align-items-center justify-content-between'>
                <span className={"pr-2"}>{notification.title}</span> <DismissButton onChange={onChange} id={notification.id}/>
            </span>
        )
    }
}

const NotificationsMenu = ({notifications, onChange, inactive}) => {
    const [showMenu, setShowMenu] = useState(false);

    const toggleNotificationseMenu = event => {
        let _showMenu = !showMenu;

        // special case for clicking on the dropdown menu trigger itself
        const parents = getDOMParents(event.target, 'button', true);
        const isTrigger = e => e.id === 'navbarDropdownNotifcationsMenuLink';
        const clickedTrigger = parents.filter(isTrigger).length > 0;

        if (_showMenu || (!_showMenu && clickedTrigger)) {
            event.stopPropagation();
        }

        setShowMenu(_showMenu);
    };

    useEffect(() => {
        if (showMenu) {
            window.addEventListener('click', toggleNotificationseMenu, true);
        } else {
            window.removeEventListener('click', toggleNotificationseMenu, true);
        }

        return function cleanup() {
            window.removeEventListener('click', toggleNotificationseMenu, true);
        };
    });

     return (
        <ul className='navbar-nav pull-right ml-auto d-print-none'>
            <li className={`nav-item dropdown`}>
                <Button
                    flavor='link'
                    className='nav-link dropdown-toggle'
                    id='navbarDropdownNotifcationsMenuLink'
                    onClick={inactive ? null : toggleNotificationseMenu}
                >
                    <Icon title="Notifications" name='bell' /><span className='ml-2 d-none d-md-inline'>{notifications.length}</span>
                    <Icon className='ml-2 d-md-none' title="Notifications" name='arrow-down' />
                </Button>
                <div
                    className={`dropdown-menu dropdown-menu-right ${
                        showMenu ? 'show' : ''
                    }`}
                >
                    {notifications.length ? notifications.map((n) =>  <NotificationItem notification={n} key={n.id} onChange={onChange}/> ) : <span className='dropdown-item text-gray-600'>No notifications.</span>}
                </div>
            </li>
        </ul>
     )
}


export const CustomerNavbar = ({
    apps,
    currentApp: currentAppName,
    brand,
    BrandHeader,
    location,
    logout,
    user,
}: NavbarProps) => {
    const currentApp = apps[currentAppName] || {};
    const siteName = brand.name || 'Portal';
    const extraNav = [
        {
            path: "/support/new",
            search: `?referral_url=${location.pathname}`,
            label: 'Help',
            icon: 'plus-circle',
        },
    ];

    return (
        <div>
            <BrandHeader key='header' />
            <Nav>
                <Link to='/' className='navbar-brand border-right pr-3'>
                    <Icon name='home' />
                </Link>

                <AppNavMenu app={currentApp} location={location} extra={extraNav} user={user}  />

                <ProfileMenu
                    user={user}
                    logout={logout}
                    profileUrl='/info/basics'
                    profileLabel='Settings'
                />
            </Nav>
            {currentApp.label && (
                <Helmet
                    titleTemplate={`${siteName} | ${currentApp.label} | %s`}
                    defaultTitle={`${siteName} | ${currentApp.label}`}
                    bodyAttributes={{ "class": currentApp.key }}
                />
            )}
        </div>
    );
};

export const SpringboardNavbar = ({
    apps,
    appTrail,
    appLocations,
    brand,
    currentApp: currentAppName,
    location,
    logout,
    toggleSwitcher,
    user,
}: NavbarProps) => {
    const currentApp = apps[currentAppName] || {};
    const siteName = brand.name || 'Portal';
    const listPath = useSelector(state => {
        const slice = state[currentApp.key] || {};
        return slice.listPath ? slice.listPath : currentApp.path;
    });

    return (
        <div className='sticky-top' style={{zIndex: 1000}}>
            <Nav>
                <a
                    href='#show-nav'
                    onClick={toggleSwitcher}
                    className='app-switcher d-print-none text-primary bg-light border-right border-gray-200'
                >
                    <Icon name='th' title='Menu' className='fa-lg' />
                </a>

                {currentApp.label && (
                    <Link className='navbar-brand' to={listPath}>
                        {currentApp.label}
                        <Helmet
                            titleTemplate={`${siteName} | ${currentApp.label} | %s`}
                            defaultTitle={`${siteName} | ${currentApp.label}`}
                            bodyAttributes={{ "class": currentApp.key }}
                        />
                    </Link>
                )}

                <AppNavMenu app={currentApp} location={location} user={user} />

                <ProfileMenu user={user} logout={logout} />
            </Nav>

            {currentApp.label && (
                <AppTrail
                    apps={apps}
                    currentApp={currentApp.key}
                    appTrail={appTrail}
                    appLocations={appLocations}
                />
            )}
        </div>
    );
};

export const GroupNavbar = ({
    apps,
    currentApp: currentAppName,
    brand,
    BrandHeader,
    location,
    logout,
    user,
    appTrail,
    appLocations,
    breadcrumbs,
}: NavbarProps) => {
    const currentApp = apps[currentAppName] || {};
    const siteName = brand.name || 'Portal';
    const appMenuNav = [];

    toPairs(apps).map(([key, app]) => {
        if (app.nav && user.apps[key]) {
            appMenuNav.push(app)
        }
    })
    appMenuNav.sort((a, b) => a.order - b.order)

    const manager = useManager({ collection });

    return (
        <div>
            <BrandHeader key='header' />
            <Nav>
                <Link to='/' className='navbar-brand border-right pr-3'>
                    <Icon name='home' />
                </Link>

                <AppNavMenu app={currentApp} location={location} extra={appMenuNav} user={user} showAppItems={false} extraClass="justify-content-end"/>

                <Fetcher
                    fetch={manager.actions.replaceItems}
                    render={({fetch}) => (
                        <NotificationsMenu notifications={manager.getItems()} onChange={fetch}/>
                    )}
                    renderFetching={() =>  <NotificationsMenu notifications={manager.getItems()} inactive={true}/> }
                />

                <ProfileMenu
                    user={user}
                    logout={logout}
                    profileUrl='/profile'
                    profileLabel='Settings'
                />
            </Nav>
            {currentApp.label && (user.apps.broker_portfolio || user.apps.group_portfolio) && (
                <>
                    <BreadcrumbTrail
                        apps={apps}
                        currentApp={currentApp.key}
                        appTrail={appTrail}
                        appLocations={appLocations}
                        breadcrumbs={breadcrumbs}
                        rightItems={<AppItemsMenu app={currentApp} location={location} user={user} />}
                    />
                    <Helmet
                        titleTemplate={`${siteName} | ${currentApp.label} | %s`}
                        defaultTitle={`${siteName} | ${currentApp.label}`}
                        bodyAttributes={{"class": currentApp.key}}
                    />
                </>
            )}
        </div>
    );
};
